/**
 * Roles, and what each one may reach.
 *
 * Two panes rather than a modal: picking a role and editing its grants is one
 * task, and the whole point of the screen is comparing what a Manager gets
 * against what a Cashier gets, which a dialog covering the list would defeat.
 *
 * The grid is deliberately not saved per checkbox. Withdrawing a screen from a
 * role is the kind of change worth reviewing before it applies, so edits are
 * held locally until Save and the unsaved state is visible while they are.
 *
 * Only the supplier's Super Admin reaches this - the API answers 404 to anyone
 * else, and App.jsx will not render the route for them either.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { FiShield, FiPlus, FiSave, FiRotateCcw, FiUsers, FiLock } from 'react-icons/fi';

import { adminApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import { PageHeader, Badge, EmptyState } from '../components/ui/Feedback';
import { Field, Input, Textarea } from '../components/ui/Field';

/** The four rights a grant carries, in the order they appear as columns. */
const RIGHTS = [
    { key: 'canView', label: 'View' },
    { key: 'canCreate', label: 'Create' },
    { key: 'canEdit', label: 'Edit' },
    { key: 'canDelete', label: 'Delete' }
];

/** A route row arrives with SQL's 1/0; the grid works in booleans. */
const toGrant = (row) => ({
    routeId: row.Route_ID,
    canView: !!row.Can_View,
    canCreate: !!row.Can_Create,
    canEdit: !!row.Can_Edit,
    canDelete: !!row.Can_Delete
});

const RolesPage = () => {
    const [selectedId, setSelectedId] = useState(null);
    const [grants, setGrants] = useState({});
    const [creating, setCreating] = useState(null);
    const [renaming, setRenaming] = useState(null);

    const loadRoles = useCallback(() => adminApi.listRoles(), []);
    const { data: roles, loading: loadingRoles, refetch: refetchRoles } = useAsync(loadRoles, []);

    // Select the first role once the list arrives, so the right pane is never
    // an empty panel next to a populated list.
    useEffect(() => {
        if (!selectedId && roles?.length) setSelectedId(roles[0].Role_ID);
    }, [roles, selectedId]);

    const loadRoutes = useCallback(
        () => (selectedId ? adminApi.getRoleRoutes(selectedId) : Promise.resolve(null)),
        [selectedId]
    );
    const { data: detail, loading: loadingRoutes, refetch: refetchRoutes } = useAsync(
        loadRoutes, [selectedId]
    );

    // Reset the working copy whenever a different role is loaded, so unsaved
    // ticks never leak from one role onto another.
    useEffect(() => {
        if (!detail?.routes) return;
        setGrants(Object.fromEntries(detail.routes.map((r) => [r.Route_ID, toGrant(r)])));
    }, [detail]);

    const selectedRole = roles?.find((r) => r.Role_ID === selectedId) || null;

    const dirty = useMemo(() => {
        if (!detail?.routes) return false;
        return detail.routes.some((row) => {
            const now = grants[row.Route_ID];
            if (!now) return false;
            const was = toGrant(row);
            return RIGHTS.some(({ key }) => was[key] !== now[key]);
        });
    }, [detail, grants]);

    const { run: save, busy: saving } = useAction(
        () => adminApi.setRoleRoutes(selectedId, Object.values(grants)),
        {
            successMessage: 'Saved. It applies at their next sign-in.',
            onSuccess: () => { refetchRoutes(); refetchRoles(); }
        }
    );

    const { run: create, busy: creatingBusy } = useAction(
        (form) => adminApi.createRole(form),
        {
            successMessage: 'Role created.',
            onSuccess: (result) => {
                setCreating(null);
                refetchRoles();
                if (result?.roleId) setSelectedId(result.roleId);
            }
        }
    );

    const { run: rename, busy: renamingBusy } = useAction(
        (form) => adminApi.updateRole(form.Role_ID, {
            roleName: form.roleName,
            roleDescription: form.roleDescription
        }),
        { successMessage: 'Role updated.', onSuccess: () => { setRenaming(null); refetchRoles(); } }
    );

    const toggle = (routeId, key, value) => setGrants((prev) => {
        const row = { ...prev[routeId], [key]: value };

        // View is what the sidebar and the route guard read, so a row granting
        // Create without View is a permission nobody can ever use. Ticking any
        // of the other three implies it; clearing View clears them with it.
        if (key === 'canView' && !value) {
            row.canCreate = false;
            row.canEdit = false;
            row.canDelete = false;
        } else if (key !== 'canView' && value) {
            row.canView = true;
        }

        return { ...prev, [routeId]: row };
    });

    const screens = (detail?.routes || []).filter((r) => r.Route_Kind === 'SCREEN');
    const actions = (detail?.routes || []).filter((r) => r.Route_Kind !== 'SCREEN');

    const renderRows = (rows) => rows.map((row) => {
        const grant = grants[row.Route_ID];
        if (!grant) return null;

        return (
            <tr key={row.Route_ID} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="py-2 pl-3 pr-2">
                    <p className="font-medium text-slate-800">{row.Route_Name}</p>
                    <p className="text-xs text-slate-500">
                        {row.Route_Path || row.Route_Code}
                    </p>
                </td>
                {RIGHTS.map(({ key }) => (
                    <td key={key} className="px-1 py-1 text-center sm:px-2 sm:py-2">
                        {/* The box itself stays small - a 44px checkbox looks
                            wrong in a grid of seventy. The label around it is
                            what a finger has to hit, so that is what carries
                            the tap size. */}
                        <label className="mx-auto flex h-11 w-11 cursor-pointer items-center justify-center
                                          rounded-lg hover:bg-slate-100 lg:h-8 lg:w-8">
                            <input
                                type="checkbox"
                                className="h-5 w-5 cursor-pointer accent-brand-600 lg:h-4 lg:w-4"
                                checked={grant[key]}
                                onChange={(e) => toggle(row.Route_ID, key, e.target.checked)}
                                aria-label={`${row.Route_Name} ${key}`}
                            />
                        </label>
                    </td>
                ))}
            </tr>
        );
    });

    return (
        <div className="page">
            <PageHeader
                title="Roles"
                subtitle="What each role can open. Changes apply at the user's next sign-in."
                actions={(
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setCreating({ roleName: '', roleDescription: '' })}
                    >
                        <FiPlus className="h-4 w-4" /> New role
                    </button>
                )}
            />

            <div className="flex min-h-0 flex-1 flex-col gap-2 lg:flex-row lg:gap-3">
                {/* Roles */}
                <div className="max-h-44 w-full shrink-0 overflow-y-auto rounded-lg border border-slate-200 bg-white lg:max-h-none lg:w-64">
                    {loadingRoles ? (
                        <div className="p-6"><Spinner label="Loading roles…" /></div>
                    ) : (roles || []).map((role) => (
                        <button
                            key={role.Role_ID}
                            type="button"
                            onClick={() => setSelectedId(role.Role_ID)}
                            className={`flex w-full flex-col gap-1 border-b border-slate-100 p-3 text-left transition
                                ${role.Role_ID === selectedId ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
                        >
                            <span className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{role.Role_Name}</span>
                                {!!role.Is_System_Role && (
                                    <FiLock className="h-3 w-3 text-slate-400" title="Built-in role" />
                                )}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                                <FiUsers className="h-3 w-3" />
                                {role.User_Count} {role.User_Count === 1 ? 'user' : 'users'}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Grants */}
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
                    {!selectedRole ? (
                        <EmptyState
                            icon={FiShield}
                            title="No role selected"
                            description="Pick a role on the left to see what it can reach."
                        />
                    ) : (
                        <>
                            <div className="flex items-center justify-between gap-3 border-b border-slate-200 p-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="truncate font-semibold text-slate-800">
                                            {selectedRole.Role_Name}
                                        </h3>
                                        {dirty && <Badge tone="amber">unsaved</Badge>}
                                    </div>
                                    <p className="truncate text-xs text-slate-500">
                                        {selectedRole.Role_Description || 'No description'}
                                    </p>
                                </div>

                                <div className="flex shrink-0 gap-2">
                                    <button
                                        type="button"
                                        className="btn-secondary btn-sm"
                                        onClick={() => setRenaming({
                                            ...selectedRole,
                                            roleName: selectedRole.Role_Name,
                                            roleDescription: selectedRole.Role_Description || ''
                                        })}
                                    >
                                        Rename
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary btn-sm"
                                        onClick={refetchRoutes}
                                        disabled={!dirty || saving}
                                        title="Discard unsaved changes"
                                    >
                                        <FiRotateCcw className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primary btn-sm"
                                        onClick={() => save()}
                                        disabled={!dirty || saving}
                                    >
                                        <FiSave className="h-4 w-4" />
                                        {saving ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </div>

                            {loadingRoutes ? (
                                <div className="p-6"><Spinner label="Loading permissions…" /></div>
                            ) : (
                                <div className="min-h-0 flex-1 overflow-auto">
                                    <table className="w-full text-sm">
                                        <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                            <tr>
                                                <th className="py-2 pl-3 pr-2 text-left font-medium">Screen</th>
                                                {RIGHTS.map(({ key, label }) => (
                                                    <th key={key} className="w-14 px-1 py-2 text-center font-medium lg:w-20 lg:px-2">
                                                        {label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {renderRows(screens)}

                                            {actions.length > 0 && (
                                                <tr className="border-t border-slate-200 bg-slate-50">
                                                    <td colSpan={RIGHTS.length + 1}
                                                        className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                                                        Actions
                                                        <span className="ml-2 normal-case tracking-normal text-slate-400">
                                                            — checked in code, never shown in the sidebar
                                                        </span>
                                                    </td>
                                                </tr>
                                            )}
                                            {renderRows(actions)}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* New role */}
            <Modal
                open={!!creating}
                onClose={() => setCreating(null)}
                title="New role"
                subtitle="Create it first, then tick what it can reach."
                width="sm"
                dismissible={!creatingBusy}
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setCreating(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => create(creating)}
                            disabled={creatingBusy || !creating?.roleName?.trim()}
                        >
                            {creatingBusy ? 'Creating…' : 'Create role'}
                        </button>
                    </>
                )}
            >
                {creating && (
                    <div className="space-y-3">
                        <Field label="Name" required>
                            <Input
                                value={creating.roleName}
                                onChange={(e) => setCreating({ ...creating, roleName: e.target.value })}
                                placeholder="Supervisor"
                                data-autofocus
                            />
                        </Field>
                        <Field label="Description" hint="What this role is for.">
                            <Textarea
                                value={creating.roleDescription}
                                onChange={(e) => setCreating({ ...creating, roleDescription: e.target.value })}
                            />
                        </Field>
                    </div>
                )}
            </Modal>

            {/* Rename */}
            <Modal
                open={!!renaming}
                onClose={() => setRenaming(null)}
                title={`Rename ${renaming?.Role_Name || ''}`}
                width="sm"
                dismissible={!renamingBusy}
                footer={(
                    <>
                        <button type="button" className="btn-secondary" onClick={() => setRenaming(null)}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={() => rename(renaming)}
                            disabled={renamingBusy || !renaming?.roleName?.trim()}
                        >
                            {renamingBusy ? 'Saving…' : 'Save'}
                        </button>
                    </>
                )}
            >
                {renaming && (
                    <div className="space-y-3">
                        <Field
                            label="Name"
                            required
                            hint={renaming.Is_System_Role
                                ? 'Built-in roles are named in the code and cannot be renamed.'
                                : undefined}
                        >
                            <Input
                                value={renaming.roleName}
                                onChange={(e) => setRenaming({ ...renaming, roleName: e.target.value })}
                                disabled={!!renaming.Is_System_Role}
                                data-autofocus
                            />
                        </Field>
                        <Field label="Description">
                            <Textarea
                                value={renaming.roleDescription}
                                onChange={(e) => setRenaming({ ...renaming, roleDescription: e.target.value })}
                            />
                        </Field>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default RolesPage;
